/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { approveCommentingAccessRequest } from 'state/thunks/commenting';

// material
import { displayModal } from '@insticator/insticator-ui'
import { Button } from '@insticator/insticator-ui/dist/material';
import Space from 'material/space/space';
import { ReactComponent as Commenting } from 'assets/icons/commenting.svg'
import { ReactComponent as ArticleSentiment } from 'assets/icons/article-sentiment.svg'
import { ReactComponent as TrendingNow } from 'assets/icons/trending-now.svg'
import { ReactComponent as PendingIcon } from 'assets/icons/hourglass.svg'
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg'
import { ReactComponent as CheckCircle } from 'assets/icons/check-circle.svg'
import ApprovePopup from '../approvepopup/approvepopup'
// styles
import injectSheet from 'react-jss'
import styles from './productcard_styles'
import { fetchAccountProducts } from 'state/thunks/account';
import { useIsAdminUser } from 'utils/session';
import { useNavigation } from 'utils/navigation';
import { approveTrendingNowV2AccessRequest } from 'state/thunks/trendingnowv2';
import { approveArticleSentimentV2AccessRequest } from 'state/thunks/articlesentimentv2';
import { addProductsV2ToAccount } from 'state/slices/account';
import { addSiteWithProductsV2 } from 'state/slices/migration';


const products = {
  'COMMENTING_V2': {
    icon: <Commenting />,
    productSettingsLink: '/dashboard/commenting/configuration',
    title: 'Commmenting',
    description: 'Enable topical conversation and community building for your readers. Significantly increase time on site, return visits, and ad monetization!'
  },
  'ARTICLE_SENTIMENT_V2': {
    icon: <ArticleSentiment />,
    productSettingsLink: '/dashboard/articlesentiment/configuration',
    title: 'Article Sentiment',
    description: 'Your readers can react to articles with a quick, one touch, emoji sentiment. Gauge their opinions on your content and gain valuable insights!'
  },
  'TRENDING_NOW_V2': {
    icon: <TrendingNow />,
    productSettingsLink: '/dashboard/trendingnow/configuration',
    title: 'Trending Now',
    description: 'Raise the visibility of popular articles to increase your readers participation and generate additional page views driving higher ad monetization!'
  }
}

/* ========== ~~~~~~~~~~ PRODUCT MANAGER: SITE SETUP : ADDITIONAL ~~~~~~~~~~ ========== */
const ProductCard = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const { userEmail } = useSelector(state => state.session)

  const { productType } = props

  const requestAccess = () => sendRequestForWidget()
  const { siteUUID, accountUUID } = useSelector(state => state.session);
  const [requestAccessProcessing, setRequestAccessProcessing] = useState(false)
  const [isApprovalPending, setIsApprovalPending] = useState(false)
  const [isApproved, setIsApproved] = useState(false)
 
  const isAdmin = useIsAdminUser()

  const requestProductAccess = useCallback(async (userEmail = null) => {
    setRequestAccessProcessing(true)
    let request = null
    if(productType === 'COMMENTING_V2') request = approveCommentingAccessRequest
    if(productType === 'TRENDING_NOW_V2') request = approveTrendingNowV2AccessRequest
    if(productType === 'ARTICLE_SENTIMENT_V2') request = approveArticleSentimentV2AccessRequest
    if(!request) {
      setRequestAccessProcessing(false)
      return
    }
    const response = await dispatch(request({accountUUID, userEmail}))
    if(response.payload && response.payload.requestStatus === 'PENDING') setIsApprovalPending(true)
    if(response.payload && response.payload.requestStatus === 'APPROVED') {
      setIsApproved(true)
      // await dispatch(fetchAccountProducts(accountUUID))
      await dispatch(addProductsV2ToAccount(productType))
      await dispatch(addSiteWithProductsV2(siteUUID))
    }
    dispatch(displayModal(false))
    setRequestAccessProcessing(false)
  }, [productType, dispatch, accountUUID, siteUUID])


  const cancel = () => dispatch(displayModal(false))

  const handleApprove = () => requestProductAccess()

  const sendRequestForWidget = () => {
    const productName = {
      COMMENTING_V2: 'Commenting',
      TRENDING_NOW_V2: 'Trending Now',
      ARTICLE_SENTIMENT_V2: 'Article Sentiment'
    }
    if(isAdmin) {
      return dispatch(displayModal(true, 
        <ApprovePopup productType={productName[productType]} handleCancel={cancel} handleApprove={handleApprove} />
      ));
    } else {
      requestProductAccess(userEmail)
    }
  };

  const {
    account: {
      accountProducts: approvedAccountProducts,
      pendingAccountProducts,
      rejectedAccountProducts
    },
  } = useSelector((state) => state);

  const productAccessStatus = useMemo(() => {
    // Always return requestaccess for article sentiment for now. Since old accounts will show as 'approved', while there is no article sentiment yet
    if (productType === 'ARTICLE_SENTIMENT') return 'requestaccess' 

    if(pendingAccountProducts.includes(productType) || isApprovalPending) return 'pending'
    if(approvedAccountProducts.includes(productType) || isApproved) return 'approved'
    if(rejectedAccountProducts.includes(productType)) return 'rejected'
    return 'requestaccess'
  },[approvedAccountProducts, isApprovalPending, isApproved, pendingAccountProducts, productType, rejectedAccountProducts])


  const { handleNavigation } = useNavigation()

  return (
    <Space klass={`productcard ${props.classes.productcard} status-${productAccessStatus}`} direction={'column'}>
      <div style={{position: 'relative', height: 0, left: '-145px'}}>
        {productAccessStatus === 'approved' ? <CheckCircle width={20} height={20} /> : <LockIcon width={20} height={20} /> }
      </div>
      <div style={{ height: '50px' }}>{products[productType].icon}</div>
      <h4>{products[productType].title}</h4>
      <p>{products[productType].description}</p>
      {productAccessStatus === 'requestaccess' &&
        <Button
          name={'requestaccess'}
          variant={'primary'}
          text={'Request Access'}
          type={'action'}
          handleClick={requestAccess}
          processing={requestAccessProcessing}
          processingText={'Requesting access..'}
        />
      }
      { productAccessStatus === 'approved' &&
        <Space klass={'productactions'} alignItems={'center'}>
          <Button
            name={'productsettings'}
            variant={'none'}
            color={'light'} 
            text={'Product Settings'}
            type={'action'}
            handleClick={() => handleNavigation(products[productType].productSettingsLink)}
          />
         {isAdmin &&  
          <Button
            name={'configureads'}
            variant={'none'}
            color={'light'} 
            text={'Configure Ads'}
            type={'action'}
            handleClick={() => handleNavigation(`${products[productType].productSettingsLink}?view=adsConfig`)}
          />
        }
        </Space>
      }
      {productAccessStatus === 'pending' &&
        <div className='status'><PendingIcon />Approval Pending</div>
      }
      {productAccessStatus === 'rejected' &&
        <div className='status'>Contact your Account Manager</div>
      }
    </Space>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ProductCard